import { useQuery } from '@tanstack/react-query';

import { ChannelMediaTypeEnum } from '@/features/broadcasters/types';
import { PaginationValues } from '@/hooks/usePagination';
import { mediaManagerClient } from '@/lib/axios';
import { ExtractFnReturnType, QueryConfig } from '@/lib/react-query';
import { useParrillaFilters } from '@/stores/parrilla-filters';
import { PaginatedResponse } from '@/types/api';

import { TProgramBroadcast } from '../types';

import { broadcastScheduleQueryKeys } from './query-keys';

type TGetBroadcastScheduleFilters = {
  order_by?: string;
  channel_id?: string;
  priorities?: number[];
  review_state?: number[];
  gte: string;
  lte: string;
  paginationValues: PaginationValues | null;
  channel_media_type?: ChannelMediaTypeEnum | '';
  deliveryDate: string[];
  program_source_id__isnull?: boolean | null;
  program_viewed_place_id?: string;
};
export async function getBroadcastSchedule({
  channel_id,
  priorities,
  review_state,
  gte,
  lte,
  paginationValues,
  channel_media_type,
  deliveryDate,
  program_source_id__isnull,
  program_viewed_place_id,
  order_by,
}: TGetBroadcastScheduleFilters): Promise<PaginatedResponse<TProgramBroadcast>> {
  const hasExplicitOrderBy = typeof order_by === 'string' && order_by.trim().length > 0;
  let orderByFilter: string;
  if (hasExplicitOrderBy) {
    orderByFilter = order_by as string;
  } else if (paginationValues?.sortBy?.id) {
    const rawId = String(paginationValues.sortBy.id);
    const primaryId =
      rawId === 'channel' ? 'channel__short_name' : rawId === 'updated_by' ? 'updated_at' : rawId;
    const primaryOrder = String(paginationValues.sortBy.order || 'asc');
    orderByFilter = `${primaryId} ${primaryOrder}`;
  } else {
    orderByFilter = 'channel__short_name asc';
  }
  return mediaManagerClient.get('api/v1/program_broadcasts', {
    params: {
      ...(orderByFilter && { order_by: orderByFilter }),
      ...(paginationValues?.offset && { offset: paginationValues.offset }),
      ...(paginationValues?.rowsPerPage && { limit: paginationValues.rowsPerPage }),
      ...(paginationValues?.query && { search: paginationValues?.query }),
      ...(channel_media_type && { channel_media_type }),
      ...(gte && { gte }),
      ...(lte && { lte }),
      ...(channel_id && { channel_id }),
      ...(priorities && priorities?.length > 0 && { priorities: priorities.join(',') }),
      ...(review_state && review_state?.length > 0 && { review_states: review_state.join(',') }),
      ...(deliveryDate &&
        deliveryDate?.length > 0 && { delivery_datetime_at__in: deliveryDate.join(',') }),
      ...(program_viewed_place_id && {
        program_viewed_place_id__in: program_viewed_place_id,
      }),
      program_source_id__isnull,
    },
  });
}

type QueryFnType = typeof getBroadcastSchedule;

interface UseResourcesParams {
  config?: QueryConfig<QueryFnType>;
}

export function useGetBroadcastSchedule({ config }: UseResourcesParams = {}) {
  const {
    order_by,
    gte,
    lte,
    priorities,
    channel_id,
    channel_media_type,
    review_state,
    paginationValues,
    deliveryDate,
    program_source_id__isnull,
    program_viewed_place_id,
  } = useParrillaFilters();
  return useQuery<ExtractFnReturnType<QueryFnType>>({
    staleTime: 0,
    ...config,
    queryKey: [
      ...broadcastScheduleQueryKeys.broadcastSchedule(),
      {
        order_by,
        priorities,
        channel_id,
        channel_media_type,
        review_state,
        gte,
        lte,
        deliveryDate,
        program_source_id__isnull,
        program_viewed_place_id,
      },
      ...Object.values(paginationValues || {}),
    ],
    queryFn: () =>
      getBroadcastSchedule({
        order_by,
        priorities,
        review_state,
        channel_id,
        gte,
        lte,
        paginationValues,
        channel_media_type,
        deliveryDate,
        program_source_id__isnull,
        program_viewed_place_id,
      }),
  });
}
